import {Page} from "@/libwyze-shared/types";
import React, {ReactElement} from "react";
import {Box, Typography, useTheme} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {CheckinScreenType, CheckinState} from "./CheckinPlayerPreviewFragment";
import {CheckinButton} from "@/checkin-shared/Components";
import {BORDER_RADIUS, PAGE_FRAGMENT_HALF_WIDTH, SZ_JUMBO, SZ_SM} from "@/shared/dimens";

function ScannerViewfinder(): ReactElement {
  const theme = useTheme();
  const accentColor = theme.palette.warning.main;
  const cornerSize = SZ_SM;
  const cornerStyle = (top: boolean, left: boolean): React.CSSProperties => ({
    position: "absolute",
    width: cornerSize,
    height: cornerSize,
    borderColor: accentColor,
    borderStyle: "solid",
    borderWidth: 0,
    ...(top ? {top: 16} : {bottom: 16}),
    ...(left ? {left: 16} : {right: 16}),
    ...(top && left ? {borderTopWidth: 2, borderLeftWidth: 2} : {}),
    ...(top && !left ? {borderTopWidth: 2, borderRightWidth: 2} : {}),
    ...(!top && left ? {borderBottomWidth: 2, borderLeftWidth: 2} : {}),
    ...(!top && !left ? {borderBottomWidth: 2, borderRightWidth: 2} : {}),
  });

  return <Box sx={{
    position: "relative",
    width: PAGE_FRAGMENT_HALF_WIDTH,
    height: PAGE_FRAGMENT_HALF_WIDTH,
    borderRadius: BORDER_RADIUS,
    backgroundColor: "black",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  }}>
    <div style={cornerStyle(true, true)}/>
    <div style={cornerStyle(true, false)}/>
    <div style={cornerStyle(false, true)}/>
    <div style={cornerStyle(false, false)}/>
    <Box sx={{
      position: "absolute",
      left: 16,
      right: 16,
      height: 2,
      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
      boxShadow: `0 0 16px 4px ${accentColor}`,
      animation: "scanLine 2s ease-in-out infinite",
      "@keyframes scanLine": {
        "0%": {top: "20%"},
        "50%": {top: "75%"},
        "100%": {top: "20%"},
      },
    }}/>
    <Typography variant="caption" sx={{
      position: "absolute",
      bottom: -28,
      opacity: 0.5,
    }}>
      Camera &middot; 720p &middot; Scanning
    </Typography>
  </Box>;
}

export function ScanQrCodeScreen(props: {
  practicePage: Page,
  pushScreenType: (st: CheckinScreenType) => void,
  checkinState: CheckinState,
  setCheckinState: (checkin: CheckinState) => void,
}): ReactElement {
  const theme = useTheme();
  return <StyledBoxRow style={{width: "100%", height: "100%", alignItems: "flex-start", gap: 48}}>
    <StyledBoxColumn style={{alignItems: "flex-start"}}>
      <Typography variant={"h2"} style={{fontWeight: 900}}>
        Hold your code up to the camera.
      </Typography>
      <Typography style={{fontSize: "100%", opacity: 0.5, marginTop: 8}}>
        The QR code is in your confirmation email, SMS or WhatsApp message. Keep it steady for a few seconds.
      </Typography>
      <StyledBoxRow style={{alignItems: "center", gap: 8, marginTop: 24}}>
        <Box sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: theme.palette.warning.main,
        }}/>
        <Typography style={{color: theme.palette.warning.main}}>
          Searching for code...
        </Typography>
      </StyledBoxRow>
      <StyledSpan/>
      <CheckinButton
        variant="outlined"
        onClick={() => {
          props.pushScreenType(CheckinScreenType.ENTER_APPOINTMENT_CODE);
        }}>
        Don't have a code? Enter manually
      </CheckinButton>
    </StyledBoxColumn>
    <Box style={{display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 16}}>
      <ScannerViewfinder/>
    </Box>
  </StyledBoxRow>;
}
